import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Sparkles, ArrowRight, BadgeCheck } from "lucide-react";
import {
  Container, Section, SectionHeading, Reveal, Eyebrow, LeafBackdrop, ImageFrame, stagger, fadeUp,
} from "@/components/common";
import { Icon } from "@/components/ui-extras";
import HomeGlobe from "@/components/HomeGlobe";
import { COMPANY, IMG, img, TRUST_STRIP, ABOUT_PARAS, WHY_CHOOSE_US } from "@/data/content";

const Home = () => {
  return (
    <div>
      {/* ============ HERO ============ */}
      <section data-testid="home-hero" className="relative overflow-hidden hero-navy-gradient">
        <LeafBackdrop className="-right-24 -top-16 opacity-60" />
        <LeafBackdrop className="-left-28 bottom-0 opacity-40" color="rgba(58,187,176,0.07)" />
        <Container wide className="relative pt-16 pb-16 sm:pt-20 sm:pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-6">
              <motion.div initial="hidden" animate="show" variants={stagger}>
                <motion.div variants={fadeUp}><Eyebrow onNavy>Government of India Recognized Star Export House</Eyebrow></motion.div>
                <motion.h1
                  variants={fadeUp}
                  className="mt-6 font-serif text-[2.6rem] leading-[1.04] sm:text-5xl lg:text-[4rem] tracking-[-0.02em] text-white"
                >
                  Global Agricultural Trade,
                  <span className="block gold-text-gradient">Built on Trust.</span>
                </motion.h1>
                <motion.p variants={fadeUp} className="mt-7 max-w-xl font-sans text-base sm:text-lg leading-relaxed" style={{ color: "var(--pt-muted-on-navy)" }}>
                  An Indian exporter of premium agricultural commodities — specializing in rice, tea, sugar, animal feed commodities, and private label export supply for buyers across the Middle East, Africa, South-East Asia, and expansion markets.
                </motion.p>
                <motion.div variants={fadeUp} className="mt-9">
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="home-hero-email-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm font-semibold transition-all"
                    style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606", boxShadow: "0 8px 24px rgba(58,187,176,0.35)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
                    onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
                  >
                    <Mail size={16} /> Email Us
                  </a>
                </motion.div>
                <motion.div variants={fadeUp} className="mt-6">
                  <span
                    data-testid="hero-iso-badge"
                    className="inline-flex items-center gap-2.5 rounded-full py-2 pl-2.5 pr-4 font-sans text-xs sm:text-[13px] font-medium"
                    style={{ background: "rgba(58,187,176,0.10)", border: "1px solid rgba(58,187,176,0.4)", color: "var(--pt-muted-on-navy)" }}
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)" }}>
                      <BadgeCheck size={15} color="#020606" />
                    </span>
                    <span><span className="font-semibold text-white">ISO 22000:2018 Certified</span> Food Safety Management System</span>
                  </span>
                </motion.div>
              </motion.div>
            </div>

            {/* Hero image stack */}
            <div className="lg:col-span-6">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }} className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4 pt-8">
                    <ImageFrame src={img(IMG.basmati, 700)} alt="Premium basmati rice grains" className="aspect-[3/4]" />
                    <ImageFrame src={img(IMG.teaGarden, 700)} alt="Tea garden rows" className="aspect-square" />
                  </div>
                  <div className="space-y-4">
                    <ImageFrame src={img(IMG.riceField, 700)} alt="Lush rice field" className="aspect-square" />
                    <ImageFrame src={img(IMG.leafTea, 700)} alt="Fresh tea leaves" className="aspect-[3/4]" />
                  </div>
                </div>
                <div className="absolute -bottom-5 -left-5 hidden sm:flex items-center gap-3 rounded-2xl px-5 py-4" style={{ background: "#fff", boxShadow: "var(--shadow-gold-glow)" }}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: "rgba(58,187,176,0.15)" }}>
                    <Icon name="Award" size={20} color="#0E8C82" />
                  </div>
                  <div>
                    <p className="font-serif text-lg leading-none" style={{ color: "var(--pt-navy)" }}>Star Export House</p>
                    <p className="font-sans text-xs mt-1" style={{ color: "var(--pt-muted)" }}>Govt. of India Recognized</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </Container>
        <div className="gold-hairline" />
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section data-testid="home-trust-strip" className="bg-paper">
        <Container wide className="py-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 divide-y sm:divide-y-0 sm:divide-x divide-[color:var(--pt-border)]">
            {TRUST_STRIP.map((t, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3 sm:py-2">
                <Sparkles size={16} color="#0E8C82" className="shrink-0" />
                <span className="font-sans text-[13px] leading-snug" style={{ color: "var(--pt-navy)" }}>{t}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ============ ABOUT US ============ */}
      <Section className="bg-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-6">
              <div className="relative">
                <ImageFrame src={img(IMG.riceSack, 900)} alt="Premium Indian rice commodity in export-grade sacks" className="aspect-[4/3]" />
                <div className="absolute -bottom-6 -right-4 hidden md:block rounded-2xl px-6 py-5 bg-paper" style={{ boxShadow: "var(--shadow-card)", border: "1px solid var(--pt-border-gold)" }}>
                  <p className="font-serif text-4xl gold-text-gradient leading-none">14+</p>
                  <p className="font-sans text-xs mt-1" style={{ color: "var(--pt-muted)" }}>Years of export experience</p>
                </div>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-6" delay={0.1}>
              <SectionHeading eyebrow="About Us" title="An India-Based Agricultural Export House" />
              <div className="mt-5 space-y-4">
                {ABOUT_PARAS.map((p, i) => (
                  <p key={i} className="font-sans text-base leading-relaxed" style={{ color: "var(--pt-muted)" }}>{p}</p>
                ))}
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* ============ GLOBAL REACH (clickable globe) ============ */}
      <section data-testid="home-global-reach" className="relative overflow-hidden hero-navy-gradient">
        <LeafBackdrop className="-left-24 top-8 opacity-40" color="rgba(58,187,176,0.07)" />
        <Container className="relative py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <Reveal className="lg:col-span-5">
              <Eyebrow onNavy>Global Reach</Eyebrow>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-[2.8rem] leading-[1.08] text-white">
                A Worldwide Agricultural Trade Network
              </h2>
              <p className="mt-5 font-sans text-base leading-relaxed" style={{ color: "var(--pt-muted-on-navy)" }}>
                From the Middle East and Africa to South-East Asia and expanding global markets — explore the regions where Prince Trading Impex delivers premium agricultural commodities.
              </p>
              <Link
                to="/markets-served"
                data-testid="home-globe-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm font-semibold transition-all"
                style={{ background: "linear-gradient(90deg,#3ABBB0,#41E19A)", color: "#020606", boxShadow: "0 8px 24px rgba(58,187,176,0.35)" }}
                onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
              >
                Explore Markets Served <ArrowRight size={16} />
              </Link>
            </Reveal>

            <Reveal className="lg:col-span-7" delay={0.1}>
              <Link
                to="/markets-served"
                aria-label="View Markets Served"
                title="View Markets Served"
                data-testid="home-globe-link"
                className="home-globe-link group relative block cursor-pointer rounded-[var(--radius)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3ABBB0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#020606]"
              >
                <div
                  className="overflow-hidden rounded-[var(--radius)] will-change-transform group-hover:scale-[1.02]"
                  style={{
                    border: "1px solid rgba(58,187,176,0.25)",
                    background: "radial-gradient(circle at 50% 40%, rgba(58,187,176,0.14), rgba(2,6,6,0) 62%)",
                    transition: "transform 300ms ease",
                  }}
                >
                  <HomeGlobe />
                </div>
                {/* teal/mint glow on hover (animates opacity only) */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-[var(--radius)] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{ boxShadow: "0 0 44px rgba(58,187,176,0.45), inset 0 0 34px rgba(65,225,154,0.12)" }}
                />
              </Link>
            </Reveal>
          </div>
        </Container>
        <div className="gold-hairline" />
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <Section className="bg-paper relative overflow-hidden">
        <LeafBackdrop className="right-0 top-10 opacity-40" color="rgba(7,17,17,0.05)" />
        <Container className="relative">
          <SectionHeading center eyebrow="Why Choose Us" title="A Partner Built for Serious Global Buyers" intro="Credibility, capability, and consistency — the foundations of every Prince Trading Impex relationship." className="mb-14" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE_US.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.05}>
                <div data-testid="why-choose-us-card" className="h-full card-hover rounded-[var(--radius)] bg-white p-6" style={{ border: "1px solid var(--pt-border)" }}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full" style={{ border: "1.5px solid var(--pt-border-gold)", background: "rgba(58,187,176,0.08)" }}>
                    <Icon name={w.icon} size={20} color="#0E8C82" />
                  </div>
                  <h3 className="mt-5 font-serif text-xl" style={{ color: "var(--pt-navy)" }}>{w.title}</h3>
                  <p className="mt-2 font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted)" }}>{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default Home;
